import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map, has } from 'lodash';
import Vue from 'vue';

import PurchaseApi from '@/extensions/shop/store/api/purchase';
import PurchasedItemApi from '@/extensions/shop/store/api/purchasedItem';
import StatusApi from '@/extensions/shop/store/api/purchaseStatus';
import ShippingMethodApi from '@/extensions/shop/store/api/shippingMethod';
import TaxApi from '@/extensions/shop/store/api/tax';
import PaymentMethodApi from '@/extensions/shop/store/api/paymentMethod';
import StockApi from '@/extensions/shop/store/api/stock';
import IndicatorApi from '@/store/api/system/indicator';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import { notifiesPurchaserByDefault } from '@/extensions/shop/purchaserNotifications';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

// Multistore: the store the admin UI currently has selected. Lists of store-owned
// records default to it, so a satellite stops showing its siblings' rows; the
// store filter widens them again.
const selectedStoreId = () => _.get(useSetupStore(pinia), 'activeCms.id', null);

const defaultForm = {
    id: undefined,
    invoice_nr: '',
    delivery_note_nr: '',
    packing_list_nr: '',
    tracking_nr: '',
    grand_total: 0,
    currency: '',
    payment_method_name: '',
    payed_at: null,
    shipping_method_name: '',
    shipped_at: null,
    canceled_at: null,
    confirmation_email_sent_at: null,
    closed_at: null,
    created_at: null,
    status: undefined,
    indicators: undefined,
    purchaser: undefined,
    statusChangeNotification: undefined,
    notify: false,
};

const defaultRules = {};

const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false
    },
    purchasedItemsPaginated: {
        loading: true
    }
};

const purchasesPaginated = {
    path: null,
    variables: {
        paginationSizes: [25, 50, 100]
    },
    filters: {},
    pageInfo: {
        total: 0,
        perPage: 25,
        currentPage: 1,
        lastPage: 0,
        selection: {
            type: 'inclusive',
            identifiers: [],
            total: 0,
        },
    },
    allColumns: [],
    activeColumns: [],
    showFilters: false,
    defaultSort: null,
    link: {},
    items: []
};

export const useShopPurchaseStore = defineStore('shopPurchase', {
    state: () => ({
        loading: true,
        purchasesPaginated: cloneDeep(purchasesPaginated),
        purchasedItemsPaginated: {
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            category: {
                loading: false,
                items: []
            },
            status: {
                loading: false,
                items: []
            },
            indicator: {
                loading: false,
                items: []
            },
            stock: {
                loading: false,
                items: []
            },
            shippingMethod: {
                loading: false,
                items: []
            },
            tax: {
                loading: false,
                items: []
            },
            paymentMethod: {
                loading: false,
                items: []
            },
            customerGroup: {
                loading: false,
                items: [],
            },
            memberTag: {
                loading: false,
                items: []
            },
        },
        history: {},
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter'),
        notifiesPurchaserByDefault: (state) => notifiesPurchaserByDefault(state.form)
    },

    actions: {
        addPaginatedPurchasesToState(data) {
            this.purchasesPaginated.items = data.items;
            this.purchasesPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.purchasesPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.purchasesPaginated.link = cloneDeep(data.config.link);
            this.purchasesPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.purchasesPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.purchasesPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.purchasesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.purchasesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.purchasesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.purchasesPaginated.filters, key);
            } else {
                Vue.set(this.purchasesPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.purchasesPaginated, 'showFilters', value);
        },

        definePathValue(value) {
            Vue.set(this.purchasesPaginated, 'path', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.purchasesPaginated.variables, 'order', key);
            Vue.set(this.purchasesPaginated.variables, 'direction', value);
        },

        // purchased items
        addPaginatedPurchasedItemsToState(data) {
            this.purchasedItemsPaginated.items = data.items;
            this.purchasedItemsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.purchasedItemsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.purchasedItemsPaginated.link = cloneDeep(data.config.link);
            this.purchasedItemsPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.purchasedItemsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        definePurchasedItemsActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.purchasedItemsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.purchasedItemsPaginated.activeColumns = activeColumns;
            }
        },

        definePurchasedItemsPerPage(perPage) {
            this.purchasedItemsPaginated.pageInfo.perPage = perPage;
        },

        definePurchasedItemsCurrentPage(currentPage) {
            this.purchasedItemsPaginated.pageInfo.currentPage = currentPage;
        },

        definePurchasedItemsFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.purchasedItemsPaginated.filters, key);
            } else {
                Vue.set(this.purchasedItemsPaginated.filters, key, value);
            }
        },

        definePurchasedItemsFilterVisibility(value) {
            Vue.set(this.purchasedItemsPaginated, 'showFilters', value);
        },

        definePurchasedItemsPaginationSizes(value) {
            Vue.set(this.purchasedItemsPaginated.variables, 'paginationSizes', value);
        },

        updatePurchasedItemsPaginationLoading(value) {
            this.ui.purchasedItemsPaginated.loading = value;
        },

        definePurchasedItemsSorting({ key, value }) {
            Vue.set(this.purchasedItemsPaginated.variables, 'order', key);
            Vue.set(this.purchasedItemsPaginated.variables, 'direction', value);
        },

        paginationBulkSelectionCheckAll({ value }) {
            let selection = this.purchasesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.purchasesPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        paginationBulkSelectionCheckVisible({ value }) {
            const items = get(this.purchasesPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.purchasesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            let selection = this.purchasesPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.purchasesPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        insertPurchase(purchase) {
            Vue.set(this, 'form', purchase);
            Vue.set(this, 'history', cloneDeep(purchase));
        },

        insertPurchaseLogs(logs) {
            Vue.set(this.form, 'statusLogs', logs);
        },

        // Patch a single sentEmail row wherever it appears in the loaded
        // purchase form — and INSERT it into the right collection if it
        // isn't there yet. Covers two timing races:
        //  - mail-tracker created the row after our initial fetch, so the
        //    engagement strip needs the snapshot to appear at all
        //  - opens/clicks/etc. update an existing row (shallow merge keeps
        //    fields we already loaded like subject + recipient_email)
        patchSentEmail(snapshot) {
            if (!snapshot || !snapshot.id || !this.form) return;
            const id = parseInt(snapshot.id);
            const mergeOrAppend = (list) => {
                if (!Array.isArray(list)) return false;
                const index = list.findIndex(entry => parseInt(entry && entry.id) === id);
                if (index !== -1) {
                    Vue.set(list, index, { ...list[index], ...snapshot });
                } else {
                    list.push({ ...snapshot });
                }
                return true;
            };

            // Polymorphic routing — direct the snapshot at the collection
            // that owns the email. Fall back to the purchase-level list when
            // the type is unknown (top-level form sentEmails).
            const emailableType = snapshot.emailable_type;
            const emailableId = snapshot.emailable_id ? parseInt(snapshot.emailable_id) : null;
            if (emailableType && emailableType.endsWith('\\Cancellation') && emailableId) {
                const cancellation = (this.form.cancellations || []).find(c => parseInt(c.id) === emailableId);
                if (cancellation) {
                    if (!Array.isArray(cancellation.sentEmails)) Vue.set(cancellation, 'sentEmails', []);
                    mergeOrAppend(cancellation.sentEmails);
                    return;
                }
            }
            if (emailableType && emailableType.endsWith('\\ShippingPackage') && emailableId) {
                const shippingPackage = (this.form.shippingPackages || []).find(p => parseInt(p.id) === emailableId);
                if (shippingPackage) {
                    if (!Array.isArray(shippingPackage.sentEmails)) Vue.set(shippingPackage, 'sentEmails', []);
                    mergeOrAppend(shippingPackage.sentEmails);
                    return;
                }
            }
            if (emailableType && emailableType.endsWith('\\Purchase') && emailableId) {
                if (parseInt(this.form.id) === emailableId) {
                    if (!Array.isArray(this.form.sentEmails)) Vue.set(this.form, 'sentEmails', []);
                    mergeOrAppend(this.form.sentEmails);
                    return;
                }
            }

            // No routing hint (or row owned by a different purchase) —
            // patch any existing match across all known collections but
            // don't fabricate a new row, we don't know where it belongs.
            const mergeOnly = (list) => {
                if (!Array.isArray(list)) return false;
                const index = list.findIndex(entry => parseInt(entry && entry.id) === id);
                if (index === -1) return false;
                Vue.set(list, index, { ...list[index], ...snapshot });
                return true;
            };
            mergeOnly(this.form.sentEmails);
            (this.form.cancellations || []).forEach(cancellation => mergeOnly(cancellation.sentEmails));
            (this.form.shippingPackages || []).forEach(shippingPackage => mergeOnly(shippingPackage.sentEmails));
        },

        updateStatusOptionsLoading(value) {
            Vue.set(this.options.status, 'loading', value);
        },

        insertStatusOptions(items) {
            Vue.set(this.options.status, 'items', items);
        },

        updateIndicatorOptionsLoading(value) {
            Vue.set(this.options.indicator, 'loading', value);
        },

        insertIndicatorOptions(items) {
            Vue.set(this.options.indicator, 'items', items);
        },

        updateStockOptionsLoading(value) {
            Vue.set(this.options.stock, 'loading', value);
        },

        insertStockOptions(stocks) {
            Vue.set(this.options.stock, 'items', stocks);
        },

        updateShippingMethodOptionsLoading(value) {
            Vue.set(this.options.shippingMethod, 'loading', value);
        },

        insertShippingMethodOptions(shippingMethods) {
            Vue.set(this.options.shippingMethod, 'items', shippingMethods);
        },

        updateTaxOptionsLoading(value) {
            Vue.set(this.options.tax, 'loading', value);
        },

        insertTaxOptions(taxes) {
            Vue.set(this.options.tax, 'items', taxes);
        },

        updatePaymentMethodOptionsLoading(value) {
            Vue.set(this.options.paymentMethod, 'loading', value);
        },

        insertPaymentMethodOptions(paymentMethods) {
            Vue.set(this.options.paymentMethod, 'items', paymentMethods);
        },

        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        updateMemberTagOptionsLoading(value) {
            Vue.set(this.options.memberTag, 'loading', value);
        },

        insertMemberTagOptions(tags) {
            Vue.set(this.options.memberTag, 'items', tags);
        },

        resetPurchasesPaginated() {
            Vue.set(this, 'purchasesPaginated', cloneDeep(purchasesPaginated));
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormLatitude({ i, value }) {
            Vue.set(this.form.purchaser.addresses[i], 'latitude', value);
        },

        setFormLongitude({ i, value }) {
            Vue.set(this.form.purchaser.addresses[i], 'longitude', value);
        },

        setFormStatus(value) {
            Vue.set(this.form, 'status', value);
        },

        setFormNotify(value) {
            Vue.set(this.form, 'notify', value);
        },

        setFormStatusChangeNotification(value) {
            Vue.set(this.form, 'statusChangeNotification', value);
        },

        setFormStatusChangeNotificationShippingMethodService(value) {
            if (!this.form.statusChangeNotification) Vue.set(this.form, 'statusChangeNotification', {});
            Vue.set(this.form.statusChangeNotification, 'shipping_method_service', value);
        },

        setFormStatusChangeNotificationShippingMethodName(value) {
            if (!this.form.statusChangeNotification) Vue.set(this.form, 'statusChangeNotification', {});
            Vue.set(this.form.statusChangeNotification, 'shipping_method_name', value);
        },

        setFormStatusChangeNotificationTrackingCode(value) {
            if (!this.form.statusChangeNotification) Vue.set(this.form, 'statusChangeNotification', {});
            Vue.set(this.form.statusChangeNotification, 'tracking_code', value);
        },

        setFormStatusChangeNotificationCommentary(value) {
            if (!this.form.statusChangeNotification) Vue.set(this.form, 'statusChangeNotification', {});
            Vue.set(this.form.statusChangeNotification, 'commentary', value);
        },

        setFormIndicators(value) {
            Vue.set(this.form, 'indicators', value);
        },

        setFormPurchaser(value) {
            Vue.set(this.form, 'purchaser', value);
        },

        setFormLatestReceipt(value) {
            Vue.set(this.form, 'latestReceipt', value);
        },

        // Async actions
        fetchPurchasesPaginated(payload = {}) {
            return PurchaseApi.fetchPurchasesPaginated({ cms: selectedStoreId(), ...payload, ...this.purchasesPaginated }).then(data => {
                this.addPaginatedPurchasesToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchPurchasedItemsPaginated(payload = {}) {
            return PurchasedItemApi.fetchPurchasedItemsPaginated({ ...payload, ...this.purchasedItemsPaginated }, this.form.id).then(data => {
                this.addPaginatedPurchasedItemsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.definePurchasedItemsActiveColumns(activeColumnKeys);
            });
        },

        processPaginationBulkAction({ action, payload, variables }) {
            return PurchaseApi.processPurchasePaginationBulkAction(action, payload, variables, this.purchasesPaginated);
        },

        fetchPurchase(id, fetchPolicy = 'cache-first') {
            return PurchaseApi.fetchPurchase({ id, cms: selectedStoreId() }, fetchPolicy).then(purchase => {
                this.insertPurchase(cloneDeep(purchase));
            });
        },

        refetchLogs() {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            return PurchaseApi.fetchPurchaseLogs(this.form.id, cms.shop.id).then(logs => {
                this.insertPurchaseLogs(cloneDeep(logs));
            });
        },

        fetchStatusOptions() {
            this.updateStatusOptionsLoading(true);
            return StatusApi.fetchList()
                .then(statuses => {
                    this.updateStatusOptionsLoading(false);
                    this.insertStatusOptions(statuses);
                })
                .catch(err => {
                    this.updateStatusOptionsLoading(false);
                    throw err;
                });
        },

        fetchIndicatorOptions() {
            this.updateIndicatorOptionsLoading(true);
            return IndicatorApi.fetchList()
                .then(indicators => {
                    this.updateIndicatorOptionsLoading(false);
                    this.insertIndicatorOptions(indicators);
                })
                .catch(err => {
                    this.updateIndicatorOptionsLoading(false);
                    throw err;
                });
        },

        fetchStockOptions(shopID) {
            this.updateStockOptionsLoading(true);
            return StockApi.fetchList(shopID)
                .then(stocks => {
                    this.updateStockOptionsLoading(false);
                    this.insertStockOptions(stocks);
                })
                .catch(err => {
                    this.updateStockOptionsLoading(false);
                    throw err;
                });
        },

        fetchShippingMethodOptions(shopID) {
            this.updateShippingMethodOptionsLoading(true);
            return ShippingMethodApi.fetchList(shopID)
                .then(shippingMethods => {
                    this.updateShippingMethodOptionsLoading(false);
                    this.insertShippingMethodOptions(shippingMethods);
                })
                .catch(err => {
                    this.updateShippingMethodOptionsLoading(false);
                    throw err;
                });
        },

        fetchTaxOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeShop.defaultTax'))
                this.insertTaxOptions([setupStore.activeShop.defaultTax]);

            this.updateTaxOptionsLoading(true);
            return TaxApi.fetchList(shopID)
                .then(taxes => {
                    this.updateTaxOptionsLoading(false);
                    this.insertTaxOptions(taxes);
                })
                .catch(err => {
                    this.updateTaxOptionsLoading(false);
                    throw err;
                });
        },

        fetchPaymentMethodOptions(shopID) {
            this.updatePaymentMethodOptionsLoading(true);
            return PaymentMethodApi.fetchList({ shop: shopID })
                .then(paymentMethods => {
                    this.updatePaymentMethodOptionsLoading(false);
                    this.insertPaymentMethodOptions(paymentMethods);
                })
                .catch(err => {
                    this.updatePaymentMethodOptionsLoading(false);
                    throw err;
                });
        },

        fetchCustomerGroupOptions(cmsID) {
            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(cmsID)
                .then((customerGroups) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch((err) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        fetchTagOptions(cmsID) {
            this.updateMemberTagOptionsLoading(true);
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateMemberTagOptionsLoading(false);
                    this.insertMemberTagOptions(tags);
                })
                .catch(err => {
                    this.updateMemberTagOptionsLoading(false);
                    throw err;
                });
        },

        updatePurchasedItemStocks(purchasedItem) {
            return PurchasedItemApi.updatePurchasedItemStocks(purchasedItem);
        },

        create(purchase) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            purchase.shop = { id: cms.shop.id };

            return PurchaseApi.create(purchase).then(purchase => {
                this.insertPurchase(cloneDeep(purchase));
                this.resetRules();
            });
        },

        update(purchase) {
            return PurchaseApi.update(purchase).then(purchase => {
                this.insertPurchase(cloneDeep(purchase));
                this.resetRules();
            });
        },

        sendInvoice(purchase) {
            return PurchaseApi.sendInvoice(purchase).then(purchase => {
                this.insertPurchase(cloneDeep(purchase));
                this.resetRules();
            });
        },

        sendConfirmation(purchase) {
            return PurchaseApi.sendConfirmation(purchase).then(purchase => {
                this.insertPurchase(cloneDeep(purchase));
                this.resetRules();
            });
        },

        sendCreditNote(cancellation) {
            return PurchaseApi.sendCreditNote(cancellation);
        },

        sendShippingPackageSent(shippingPackage) {
            return PurchaseApi.sendShippingPackageSent(shippingPackage);
        },

        sendStatusEmail(input) {
            return PurchaseApi.sendStatusEmail(input);
        },

        reset() {
            this.insertPurchase(cloneDeep(defaultForm));
        }
    }
});
